<template>
    <div
        v-if="visible"
        class="atcw-root"
        role="dialog"
        aria-modal="true"
        :aria-label="$_locale('addedToCart')">
        <div
            class="atcw-backdrop"
            @click="close"></div>

        <div class="atcw-widget">
            <div class="atcw-card">
                <button
                    type="button"
                    class="atcw-close"
                    :aria-label="$_locale('close')"
                    @click="close">
                    ×
                </button>

                <div class="atcw-header">
                    <div class="atcw-header-status">
                        <span class="atcw-header-icon">✓</span>
                        <span class="atcw-header-title">
                            {{ $_locale('The product added to cart') }}
                        </span>
                    </div>
                </div>

                <div class="atcw-body">
                    <div
                        v-for="(product, index) in visibleProducts"
                        :key="product.cartProductId || index"
                        class="atcw-item">
                        <div class="atcw-item-media">
                            <a
                                v-if="product.image"
                                :href="product.productPageUrl || null"
                                class="atcw-item-link"
                                :aria-label="product.name">
                                <img
                                    :src="product.image"
                                    :alt="product.name"
                                    class="atcw-item-image">
                            </a>

                            <div
                                v-else
                                class="atcw-item-image atcw-item-image-placeholder"></div>
                        </div>

                        <div class="atcw-item-content">
                            <div class="atcw-item-name">
                                <a
                                    v-if="product.productPageUrl"
                                    :href="product.productPageUrl"
                                    class="atcw-item-name-link">{{ product.name || '-' }}</a>
                                <template v-else>{{ product.name || '-' }}</template>
                            </div>

                            <div
                                v-if="product.variantName"
                                class="atcw-item-variant">
                                {{ product.variantName }}
                            </div>

                            <div class="atcw-item-meta">
                                {{ $_locale('quantity') }} {{ product.quantity }}
                            </div>

                            <div class="atcw-item-price">
                                {{ formatPrice(product.price) }}
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="extraProductsCount > 0"
                        class="atcw-extra">
                        {{ extraProductsText }}
                    </div>
                </div>

                <div class="atcw-actions">
                    <button
                        type="button"
                        class="atcw-btn atcw-btn-secondary"
                        @click="goToCart">
                        {{ $_locale('cart') }}
                    </button>

                    <button
                        type="button"
                        class="atcw-btn atcw-btn-primary"
                        @click="goToCheckout">
                        {{ $_locale('Checkout') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { on } from '../common/events.js'

export default {
    name: 'AddToCartWidget',

    data() {
        return {
            visible: false,
            products: [],
            timeoutId: null,
            maxVisibleProducts: 2
        };
    },

    computed: {
        visibleProducts() {
            return this.products.slice(0, this.maxVisibleProducts);
        },

        extraProductsCount() {
            return Math.max(this.products.length - this.maxVisibleProducts, 0);
        },

        extraProductsText() {
            if (!this.extraProductsCount) {
                return '';
            }

            return '+' + this.extraProductsCount + ' ' + this.$_locale('other products added');
        }
    },

    mounted() {
        on(document.body, 'wisenetwork:addToCartSuccess', this.onAddToCartSuccess);
    },

    beforeDestroy() {
        this.clearTimer();
    },

    methods: {
        onAddToCartSuccess(evt, data) {
            const successfulProducts = (data?.products || []).filter(product => product.success);

            if (!successfulProducts.length) {
                return;
            }

            this.products = successfulProducts;
            this.visible = true;
            this.restartTimer();
        },

        close() {
            this.visible = false;
            this.clearTimer();
        },

        goToCart() {
            window.location.href = '/cart';
        },

        goToCheckout() {
            window.location.href = '/payment/checkout';
        },

        formatPrice(price) {
            if (price === null || price === undefined || price === '') {
                return '-';
            }

            return String(price).replace('.', ',') + ' €';
        },

        restartTimer() {
            this.clearTimer();

            this.timeoutId = setTimeout(() => {
                this.visible = false;
            }, 15000);
        },

        clearTimer() {
            if (this.timeoutId) {
                clearTimeout(this.timeoutId);
                this.timeoutId = null;
            }
        }
    }
};
</script>

<style>
.atcw-root {
    position: fixed;
    inset: 0;
    z-index: 2147483647;
    pointer-events: none;
}

.atcw-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(17, 17, 17, 0.22);
    backdrop-filter: blur(2px);
    pointer-events: auto;
}

.atcw-widget {
    position: absolute;
    right: 20px;
    bottom: 20px;
    width: 420px;
    max-width: calc(100vw - 40px);
    pointer-events: auto;
}

.atcw-card {
    position: relative;
    background: #ffffff;
    color: #111111;
    border-radius: 24px;
    box-shadow:
        0 24px 60px rgba(0, 0, 0, 0.18),
        0 8px 24px rgba(0, 0, 0, 0.10);
    overflow: hidden;
    padding: 24px;
}

.atcw-close {
    position: absolute;
    top: 14px;
    right: 14px;
    width: 38px;
    height: 38px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #111111;
    font-size: 26px;
    line-height: 1;
    cursor: pointer;
}

.atcw-header {
    padding-right: 44px;
    margin-bottom: 20px;
}

.atcw-header-status {
    display: flex;
    align-items: center;
    gap: 10px;
}

.atcw-header-icon {
    width: 22px;
    height: 22px;
    border-radius: 999px;
    background: #198754;
    color: #ffffff;
    font-size: 13px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 22px;
}

.atcw-header-title {
    font-size: 20px;
    font-weight: 600;
    line-height: 1.3;
    color: #111111;
}

.atcw-item {
    display: flex;
    align-items: flex-start;
    gap: 14px;
}

.atcw-item + .atcw-item {
    margin-top: 16px;
}

.atcw-item-media {
    width: 84px;
    min-width: 84px;
}

.atcw-item-image {
    display: block;
    width: 84px;
    height: 84px;
    object-fit: cover;
    border-radius: 14px;
    background: #f3f3f3;
}

.atcw-item-image-placeholder {
    background: #f1f1f1;
}

.atcw-item-content {
    min-width: 0;
    flex: 1 1 auto;
    padding-top: 2px;
}

.atcw-item-link {
    display: block;
    line-height: 0;
}

.atcw-item-name {
    font-size: 16px;
    font-weight: 600;
    line-height: 1.35;
    color: #111111;
    margin-bottom: 2px;
    word-break: break-word;
}

.atcw-item-name-link {
    color: inherit;
    text-decoration: none;
}

.atcw-item-name-link:hover {
    text-decoration: underline;
}

.atcw-item-variant {
    font-size: 14px;
    line-height: 1.45;
    color: #6b7280;
    margin-bottom: 2px;
    word-break: break-word;
}

.atcw-item-meta {
    font-size: 14px;
    line-height: 1.45;
    color: #6b7280;
    margin-bottom: 2px;
    word-break: break-word;
}

.atcw-item-price {
    margin-top: 8px;
    font-size: 16px;
    line-height: 1.35;
    font-weight: 600;
    color: #111111;
}

.atcw-extra {
    margin-top: 14px;
    font-size: 14px;
    line-height: 1.4;
    color: #6b7280;
    font-weight: 600;
}

.atcw-actions {
    display: grid;
    gap: 10px;
    margin-top: 18px;
}

.atcw-btn {
    appearance: none;
    border: 1px solid transparent;
    border-radius: 999px;
    min-height: 52px;
    padding: 12px 18px;
    font-size: 16px;
    font-weight: 600;
    text-align: center;
    cursor: pointer;
}

.atcw-btn-primary {
    background: #111111;
    color: #ffffff;
    border-color: #111111;
}

.atcw-btn-secondary {
    background: #ffffff;
    color: #111111;
    border-color: #cfcfcf;
}

@media (max-width: 640px) {
    .atcw-widget {
        right: 20px;
        left: 20px;
        bottom: 20px;
        width: auto;
        max-width: none;
    }

    .atcw-card {
        padding: 20px 16px 16px;
    }

    .atcw-item-media {
        width: 72px;
        min-width: 72px;
    }

    .atcw-item-image {
        width: 72px;
        height: 72px;
    }
}
</style>
